import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminPlatformConfiguration.css";

type ReadinessStatus="PASS"|"ATTENTION"|"BLOCKED"|"INFO"|string;

type DomainRow={
  environment:string;
  domainKey:string;
  hostname:string;
  experience:string;
  plane:string;
  authRequired:boolean;
  targetType:string;
  dnsStatus:string;
  tlsRequired:boolean;
  canonicalBaseUrl:string;
  notes?:string|null;
};

type IssuerClient={
  clientKey:string;
  keyId:string;
  status:string;
  tokenConfigured:boolean;
  publicJwkConfigured:boolean;
  keyType:string;
  algorithm:string;
};

type HealthEndpoint={key:string;label:string;url:string};

type PlatformConfiguration={
  environment:{
    appEnvironment:string;
    baseDomain:string;
    productionBaseDomain:string;
    supabaseProjectRef:string;
    databaseVersion:string;
    databaseName:string;
  };
  auth:{
    googleIdentityCount:number;
    googleLoginObserved:boolean;
  };
  domains:DomainRow[];
  issuer:{
    runtimeReady:boolean;
    webhookSecretConfigured:boolean;
    signedArtifactCount:number;
    lastSignedAt?:string|null;
    clients:IssuerClient[];
  };
  email:{
    provider:string;
    resendApiKeyConfigured:boolean;
    fromAddressConfigured:boolean;
    providerConfigured:boolean;
    badgeWorkerTokenConfigured:boolean;
    notificationWorkerTokenConfigured:boolean;
  };
  runtime:{
    pgNetInstalled:boolean;
    pgCronInstalled:boolean;
    pgauditInstalled:boolean;
    schedulingMode:string;
    edgeHealthEndpoints:HealthEndpoint[];
  };
  readiness:Array<{
    key:string;
    group:string;
    label:string;
    status:ReadinessStatus;
    detail:string;
  }>;
};

type WorkerHealth={
  key:string;
  label:string;
  url:string;
  state:"CHECKING"|"PASS"|"ATTENTION"|"OFFLINE";
  service?:string;
  provider?:string;
  providerConfigured?:boolean;
  workerTokenConfigured?:boolean;
  message?:string;
};

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function readinessLabel(value:string){
  if(value==="PASS")return "정상";
  if(value==="ATTENTION")return "확인";
  if(value==="BLOCKED")return "미구성";
  if(value==="INFO")return "정보";
  return value;
}

function domainStatusLabel(value:string){
  if(value==="ACTIVE")return "ACTIVE";
  if(value==="UNVERIFIED")return "미검증";
  return value;
}

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function AdminPlatformConfiguration({client}:{client:PlatformClient}){
  const [data,setData]=useState<PlatformConfiguration|null>(null);
  const [workerHealth,setWorkerHealth]=useState<WorkerHealth[]>([]);
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_platform_configuration").then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as PlatformConfiguration;
      setData(next);
      setWorkerHealth(next.runtime.edgeHealthEndpoints.map((item)=>({
        ...item,state:"CHECKING" as const
      })));
    });
    return()=>{active=false;};
  },[client,refresh]);

  useEffect(()=>{
    if(!data?.runtime.edgeHealthEndpoints.length)return;
    let active=true;

    Promise.all(data.runtime.edgeHealthEndpoints.map(async(endpoint)=>{
      try{
        const response=await fetch(endpoint.url,{method:"GET",cache:"no-store"});
        const body=await response.json().catch(()=>({}));
        const ok=response.ok && body?.ok===true;
        return {
          ...endpoint,
          state:(ok ? (body.providerConfigured && body.workerTokenConfigured ? "PASS" : "ATTENTION") : "OFFLINE") as WorkerHealth["state"],
          service:String(body?.service || ""),
          provider:String(body?.provider || ""),
          providerConfigured:Boolean(body?.providerConfigured),
          workerTokenConfigured:Boolean(body?.workerTokenConfigured),
          message:ok ? undefined : `HTTP ${response.status}`
        };
      }catch(err){
        return {
          ...endpoint,
          state:"OFFLINE" as const,
          message:err instanceof Error ? err.message : "Health check failed"
        };
      }
    })).then((items)=>{if(active)setWorkerHealth(items);});

    return()=>{active=false;};
  },[data,refresh]);

  const readinessGroups=useMemo(()=>{
    const map=new Map<string,PlatformConfiguration["readiness"]>();
    for(const item of data?.readiness || []){
      map.set(item.group,[...(map.get(item.group) || []),item]);
    }
    return Array.from(map.entries());
  },[data]);

  if(!data)return <p className="muted">플랫폼 설정 상태를 불러오는 중…</p>;

  const blocking=data.readiness.filter((item)=>item.status==="BLOCKED");
  const attention=data.readiness.filter((item)=>item.status==="ATTENTION");
  const pass=data.readiness.filter((item)=>item.status==="PASS");
  const devDomains=data.domains.filter((item)=>item.environment==="DEVELOPMENT");
  const prodDomains=data.domains.filter((item)=>item.environment==="PRODUCTION");

  return (
    <section className="platform-config-page">
      <header className="platform-config-hero">
        <div>
          <p className="eyebrow">PLATFORM CONFIGURATION & INTEGRATION CENTER</p>
          <h2>플랫폼 설정·연동센터</h2>
          <p>
            Google OAuth, 도메인, Supabase, 이메일 Provider, Worker, Issuer/Signing과
            운영 도메인 전환 준비상태를 비밀값 노출 없이 확인합니다.
          </p>
        </div>
        <div className={"platform-config-posture "+(blocking.length?"blocked":attention.length?"attention":"ready")}>
          <span>CUTOVER READINESS</span>
          <strong>{blocking.length?"구성 필요":attention.length?"전환 준비 중":"준비 완료"}</strong>
          <small>PASS {pass.length} · 확인 {attention.length} · 미구성 {blocking.length}</small>
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      <div className="platform-config-summary">
        <ConfigKpi label="Google Identity" value={data.auth.googleIdentityCount} sub={data.auth.googleLoginObserved?"실로그인 관찰":"로그인 관찰 없음"} />
        <ConfigKpi label="Signed Artifact" value={data.issuer.signedArtifactCount} sub={data.issuer.lastSignedAt?formatDate(data.issuer.lastSignedAt):"발급 이력 없음"} />
        <ConfigKpi label="DEV Active Domain" value={devDomains.filter((item)=>item.dnsStatus==="ACTIVE").length} sub={`${devDomains.length}개 등록`} />
        <ConfigKpi label="PROD Active Domain" value={prodDomains.filter((item)=>item.dnsStatus==="ACTIVE").length} sub={`${prodDomains.length}개 등록`} />
      </div>

      <section className="platform-config-environment">
        <div>
          <span>APP ENV</span><strong>{data.environment.appEnvironment}</strong>
        </div>
        <div>
          <span>DEV BASE</span><strong>{data.environment.baseDomain}</strong>
        </div>
        <div>
          <span>PRODUCTION TARGET</span><strong>{data.environment.productionBaseDomain}</strong>
        </div>
        <div>
          <span>SUPABASE</span><strong>{data.environment.supabaseProjectRef}</strong>
        </div>
        <div>
          <span>POSTGRES</span><strong>{data.environment.databaseVersion}</strong>
        </div>
        <button className="secondary" onClick={()=>setRefresh((value)=>value+1)}>상태 다시 확인</button>
      </section>

      <div className="platform-config-main-grid">
        <section className="config-panel readiness-panel">
          <div className="config-panel-head">
            <div><p className="eyebrow">READINESS MATRIX</p><h3>통합 준비상태</h3></div>
            <span>{data.readiness.length} checks</span>
          </div>

          <div className="config-readiness-groups">
            {readinessGroups.map(([group,items])=>(
              <div className="config-readiness-group" key={group}>
                <strong>{group}</strong>
                <div>
                  {items.map((item)=>(
                    <article key={item.key}>
                      <span className={"config-state "+item.status.toLowerCase()}>{readinessLabel(item.status)}</span>
                      <div><b>{item.label}</b><small>{item.detail}</small></div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="config-panel">
          <div className="config-panel-head">
            <div><p className="eyebrow">EDGE WORKER HEALTH</p><h3>실행 Worker</h3></div>
            <button className="text-button" onClick={()=>navigate("/system-operations")}>Job 관제 →</button>
          </div>

          <div className="worker-health-list">
            {workerHealth.map((item)=>(
              <article key={item.key}>
                <span className={"worker-health-state "+item.state.toLowerCase()}>
                  {item.state==="CHECKING"?"확인 중":item.state==="PASS"?"정상":item.state==="ATTENTION"?"Provider 확인":"응답 실패"}
                </span>
                <div>
                  <strong>{item.label}</strong>
                  <small>{item.service || item.url}</small>
                  {item.state!=="CHECKING" && (
                    <p>
                      Token {item.workerTokenConfigured?"✓":"✕"} · Provider {item.providerConfigured?"✓":"✕"}
                      {item.message?" · "+item.message:""}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>

          <div className="config-secret-status">
            <SecretRow label="Badge Worker Token" configured={data.email.badgeWorkerTokenConfigured} />
            <SecretRow label="Notification Worker Token" configured={data.email.notificationWorkerTokenConfigured} />
            <SecretRow label="Resend API Key" configured={data.email.resendApiKeyConfigured} />
            <SecretRow label="Email From Address" configured={data.email.fromAddressConfigured} />
            <SecretRow label="Issuer Webhook Secret" configured={data.issuer.webhookSecretConfigured} />
          </div>
          <p className="config-security-note">비밀값은 이 화면으로 전달하지 않습니다. 구성 여부만 서버에서 확인합니다.</p>
        </section>
      </div>

      <section className="config-panel">
        <div className="config-panel-head">
          <div><p className="eyebrow">DOMAIN ROUTING</p><h3>개발 → 운영 도메인 전환</h3></div>
          <span>BASE_DOMAIN cutover</span>
        </div>

        <div className="domain-cutover-columns">
          <DomainTable title="DEVELOPMENT · daily-coach-ing.com" rows={devDomains} />
          <div className="domain-cutover-arrow">→</div>
          <DomainTable title="PRODUCTION · rsedu.co.kr" rows={prodDomains} />
        </div>
      </section>

      <div className="platform-config-main-grid">
        <section className="config-panel">
          <div className="config-panel-head">
            <div><p className="eyebrow">IDENTITY</p><h3>Google OAuth</h3></div>
            <span className={data.auth.googleLoginObserved?"config-state pass":"config-state attention"}>
              {data.auth.googleLoginObserved?"실로그인 확인":"확인 필요"}
            </span>
          </div>
          <div className="integration-detail-list">
            <DetailRow label="Provider" value="Google" />
            <DetailRow label="관찰된 Identity" value={String(data.auth.googleIdentityCount)} />
            <DetailRow label="대상 Experience" value="Admin · Center · Wallet" />
            <DetailRow label="Auth backend" value="Supabase Auth · PKCE" />
          </div>
        </section>

        <section className="config-panel">
          <div className="config-panel-head">
            <div><p className="eyebrow">OPEN BADGES 3.0</p><h3>Issuer & Signing</h3></div>
            <span className={data.issuer.runtimeReady?"config-state pass":"config-state blocked"}>
              {data.issuer.runtimeReady?"Runtime 정상":"구성 필요"}
            </span>
          </div>

          {data.issuer.clients.map((clientRow)=>(
            <div className="issuer-config-card" key={clientRow.clientKey}>
              <div><strong>{clientRow.clientKey}</strong><span>{clientRow.status}</span></div>
              <dl>
                <div><dt>Key ID</dt><dd>{clientRow.keyId}</dd></div>
                <div><dt>Public JWK</dt><dd>{clientRow.publicJwkConfigured?"구성":"미구성"}</dd></div>
                <div><dt>Runtime Token</dt><dd>{clientRow.tokenConfigured?"구성":"미구성"}</dd></div>
                <div><dt>Key Type</dt><dd>{clientRow.keyType || "RSA"}</dd></div>
              </dl>
            </div>
          ))}

          <div className="integration-detail-list">
            <DetailRow label="Signed artifact" value={data.issuer.signedArtifactCount+"개"} />
            <DetailRow label="마지막 Signing" value={formatDate(data.issuer.lastSignedAt)} />
            <DetailRow label="Public issuer" value="verify.daily-coach-ing.com/issuer" />
            <DetailRow label="Proof" value="VC-JWT · RS256" />
          </div>
        </section>
      </div>

      <section className="config-panel email-config-panel">
        <div className="config-panel-head">
          <div><p className="eyebrow">EMAIL PROVIDER</p><h3>Resend 전달 구성</h3></div>
          <span className={data.email.providerConfigured?"config-state pass":"config-state blocked"}>
            {data.email.providerConfigured?"구성 완료":"미구성"}
          </span>
        </div>
        <p>
          Worker는 배포되어 있고 Token도 준비되어 있습니다.
          이메일 전송에는 Vault의 <code>rs_career_resend_api_key</code>와 <code>rs_career_badge_email_from</code>이 모두 필요합니다.
        </p>
        <div className="email-config-flow">
          <span>Credential / Notification</span><i>→</i><span>DB Queue</span><i>→</i>
          <span>Edge Worker</span><i>→</i><span className={data.email.providerConfigured?"ready":"blocked"}>Resend</span><i>→</i><span>학생·교사 Email</span>
        </div>
      </section>
    </section>
  );
}

function ConfigKpi({label,value,sub}:{label:string;value:number;sub:string}){
  return <div className="config-kpi"><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong><small>{sub}</small></div>;
}

function SecretRow({label,configured}:{label:string;configured:boolean}){
  return <div><span>{label}</span><strong className={configured?"configured":"missing"}>{configured?"CONFIGURED":"MISSING"}</strong></div>;
}

function DetailRow({label,value}:{label:string;value:string}){
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function DomainTable({title,rows}:{title:string;rows:DomainRow[]}){
  return (
    <div className="domain-config-table">
      <strong>{title}</strong>
      <div className="domain-config-header"><span>Experience</span><span>Hostname</span><span>Target</span><span>Status</span></div>
      {rows.map((row)=>(
        <div className="domain-config-row" key={row.environment+":"+row.domainKey}>
          <span>{row.experience}</span>
          <b>{row.hostname}</b>
          <span>{row.targetType}</span>
          <span className={"domain-status "+row.dnsStatus.toLowerCase()}>{domainStatusLabel(row.dnsStatus)}</span>
        </div>
      ))}
    </div>
  );
}
